import { FetchDataProps, LazyData } from "types/datacontext";
import { fetchFilters, fetchSessions } from "api/webservice/sessions";
import { useMutation, QueryFunctionContext, QueryClient, useQueryClient} from "@tanstack/react-query";
import { Session } from "types";
import * as API from "api/webservice/sessions";
import { addDefaultFilters, constructProps } from "utils/repo";


// QueryKey Factory
// LazyDataTable accepts either a QueryKey from react-query
// or a function that accepts FetchDataProps and returns a
// QueryKey. Here we provide a factory function for the function
// that LazyDataTable expects. Using a factory function lets us
// include default values and data-specific props that are not accounted
// for by FetchDataProps
// Accepts an owned property, and a defaults property.
export const queryKeys = {
  data: (owned = false, defaults?: Partial<FetchDataProps>) =>
    (props?: FetchDataProps) =>
      ['sessions', owned, constructProps(props, defaults)],
  filters: (owned = false) => ['sessions', 'filters', owned],
}

// QueryFn Factory
// notice that these functions take the queryKey as an argument.
// This means that FetchDataProps from LazyDataTable will come in
// automagically.
export const queryFns = {
  data: ({ queryKey }: QueryFunctionContext) => {
    const [, owned, props] = queryKey;
    const { pageIndex, pageSize, filters, order, search, defaultFilters } = props as FetchDataProps || {};
    return fetchSessions(pageIndex, pageSize, owned as boolean, order, addDefaultFilters(props as FetchDataProps), search);
  },
  filters: ({ queryKey }: QueryFunctionContext) => {
    const [, , owned] = queryKey;
    return fetchFilters(owned as boolean);
  }
}

// Mutations
const updateSessionData = (queryClient: QueryClient) => {
  return async (session: Session) => {
    await queryClient.cancelQueries({ queryKey: ['sessions'] });

    const data = queryClient.getQueryData(['sessions']) as LazyData<Session>;

    if (data) {
      const sessionIndex = data.entries.findIndex((s) => s.id == session.id);
      const updatedSessions: LazyData<Session> = {
        entryCount: data.entryCount + 1,
        entries: [...data.entries],
        pageCount: data.pageCount
      };

      if (sessionIndex >= 0) {
        updatedSessions.entries[sessionIndex] = session;
      }
      else {
        updatedSessions.entries.unshift(session);
        updatedSessions.entries.pop();
      }

      queryClient.setQueryData(['sessions'], updatedSessions);
    }
    return { previousData: data }
  };
}

interface Callbacks {
  onError: (err: any, session: Session, context: any) => void;
  onSettled: () => void;
}

const useSessionMutationFactory = (mutationFn: (session: Session) => Promise<Session>,
                                   { onError=(err, session, context) => {},
                                     onSettled=()=>{}}: Partial<Callbacks>) => {
  const queryClient = useQueryClient();
  return  useMutation({
    mutationFn: mutationFn,
    onMutate: updateSessionData(queryClient),
    onError: (err, session, context) => {
      queryClient.setQueryData(['sessions'], context?.previousData);
      onError(err, session, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['sessions']});
      onSettled();
    }
  })
};


export const useCreateSession = (callbacks={}) =>  useSessionMutationFactory(API.createSession, callbacks);
export const useUpdateSession = (callbacks={}) => useSessionMutationFactory(API.updateSession, callbacks);
export const useCancelSession = (callbacks={}) => useSessionMutationFactory(API.cancelSession, callbacks);
export const useCompleteSession = (callbacks={}) => useSessionMutationFactory(API.completeSession, callbacks);
